import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Archive,
  ImageOff,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/components/ui/confirm';
import { cn } from '@/lib/utils';
import {
  useSetStickerListed,
  useSetStickerPackListed,
  useStickerLibrary,
  useStickerArchive,
  type MediaSticker,
  type MediaStickerPack,
} from '../api';
import { CurationStatusBadge, StickerSourceBadge } from '../components/MediaBadges';
import { StickerPackDialog } from '../components/StickerPackDialog';
import { StickerUploadDialog } from '../components/StickerUploadDialog';
import { StickerReleaseDialog } from '../components/StickerReleaseDialog';
import { StickerMetadataDialog } from '../components/StickerMetadataDialog';
import { matchesSticker, sortStickerPacks } from '../stickerSearch';

/**
 * Sticker packs and their contents — the only media kind that is uploaded here.
 * Effects and transitions are authored by the render engine and arrive through
 * the tfx draft → review → publish chain instead.
 */
export function StickerLibraryPage() {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const active = useStickerLibrary();
  const archive = useStickerArchive(archiveOpen);
  const { data, isLoading, isFetching, isError, error, refetch } = archiveOpen ? archive : active;
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [packDialog, setPackDialog] = useState<MediaStickerPack | 'new' | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [editingSticker, setEditingSticker] = useState<MediaSticker | null>(null);
  const [query, setQuery] = useState('');
  const [searchCurrentPack, setSearchCurrentPack] = useState(false);
  const [showDelisted, setShowDelisted] = useState(false);

  const packs = useMemo(
    () =>
      sortStickerPacks(data?.packs ?? []).filter(
        (pack) => archiveOpen || showDelisted || pack.status === 'ACTIVE',
      ),
    [data, archiveOpen, showDelisted],
  );
  const stickers = data?.stickers ?? [];

  // Keep a selection valid across refetches without fighting the user's choice.
  useEffect(() => {
    if (packs.length === 0) {
      setSelectedPackId(null);
      return;
    }
    setSelectedPackId((current) =>
      current && packs.some((p) => p.packId === current) ? current : (packs[0]?.packId ?? null),
    );
  }, [packs]);

  const selectedPack = packs.find((p) => p.packId === selectedPackId) ?? null;
  const searching = query.trim().length > 0;
  const packStickers = stickers
    .filter(
      (sticker) =>
        packs.some((pack) => pack.packId === sticker.packId) &&
        ((searching && !searchCurrentPack) || sticker.packId === selectedPackId) &&
        matchesSticker(sticker, query, packs),
    )
    .sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.stickerId.localeCompare(b.stickerId),
    );

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Stickers</h1>
        <span className="text-sm text-muted-foreground">
          {packs.length} groups /{' '}
          {archiveOpen ? stickers.length : stickers.filter((s) => s.status === 'ACTIVE').length}{' '}
          {archiveOpen ? 'archived stickers' : 'live stickers'}
        </span>
        <div className="flex-1" />
        <Button
          variant={archiveOpen ? 'default' : 'outline'}
          size="sm"
          onClick={() => setArchiveOpen((value) => !value)}
        >
          <Archive className="size-4" />
          {archiveOpen ? 'Show current library' : 'Archive'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={isFetching ? 'size-4 animate-spin' : 'size-4'} />
          Reload
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={archiveOpen}
          onClick={() => setPackDialog('new')}
        >
          <Plus className="size-4" />
          New pack
        </Button>
        <Button
          size="sm"
          disabled={archiveOpen || !selectedPack || selectedPack.status !== 'ACTIVE'}
          title={
            selectedPack && selectedPack.status !== 'ACTIVE'
              ? 'Relist the pack before uploading into it'
              : undefined
          }
          onClick={() => setUploadOpen(true)}
        >
          <Upload className="size-4" />
          Upload stickers
        </Button>
        <Button size="sm" onClick={() => setReleaseOpen(true)}>
          Import replacement
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <Input
            aria-label="Search stickers"
            className="pl-9 pr-9"
            placeholder="Search names, keywords, styles or groups"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setQuery('')}
              className="absolute right-3 top-2.5"
            >
              <X className="size-4" />
            </button>
          )}
        </div>
        {searching && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={searchCurrentPack}
              onChange={(e) => setSearchCurrentPack(e.target.checked)}
            />
            Only this group
          </label>
        )}
        {searching && (
          <span className="text-sm text-muted-foreground">{packStickers.length} results</span>
        )}
        {!archiveOpen && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showDelisted}
              onChange={(event) => setShowDelisted(event.target.checked)}
            />
            Show delisted groups
          </label>
        )}
      </div>
      {archiveOpen && (
        <p className="text-sm text-muted-foreground">
          Retired artwork is kept for existing drafts. It is excluded from the current mobile
          library and search.
        </p>
      )}

      {isError && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <div className="flex items-center gap-2">
            <AlertCircle className="size-4 shrink-0" />
            <span>{error instanceof Error ? error.message : 'Failed to load the library.'}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && packs.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-4">
          <aside className="w-72 shrink-0 space-y-2 overflow-y-auto rounded-md border p-2">
            {packs.length === 0 ? (
              <p className="p-4 text-center text-sm text-muted-foreground">
                No packs yet. Create one to start uploading.
              </p>
            ) : (
              packs.map((pack) => (
                <PackRow
                  key={pack.packId}
                  pack={pack}
                  selected={pack.packId === selectedPackId}
                  onSelect={() => setSelectedPackId(pack.packId)}
                  onEdit={() => setPackDialog(pack)}
                  readOnly={archiveOpen}
                />
              ))
            )}
          </aside>

          <section className="min-h-0 flex-1 overflow-y-auto rounded-md border p-4">
            {!selectedPack ? (
              <p className="py-16 text-center text-sm text-muted-foreground">
                Select a pack to see its stickers.
              </p>
            ) : packStickers.length === 0 ? (
              <p className="py-16 text-center text-sm text-muted-foreground">
                {searching
                  ? 'No stickers match your search.'
                  : `${selectedPack.displayName} has no stickers yet.`}
              </p>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3">
                {packStickers.map((sticker) => (
                  <StickerTile
                    key={sticker.stickerId}
                    sticker={sticker}
                    readOnly={archiveOpen}
                    onEdit={() => setEditingSticker(sticker)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      <StickerPackDialog
        pack={packDialog === 'new' ? null : packDialog}
        open={packDialog !== null}
        onClose={() => setPackDialog(null)}
      />
      <StickerReleaseDialog open={releaseOpen} onClose={() => setReleaseOpen(false)} />
      <StickerMetadataDialog sticker={editingSticker} onClose={() => setEditingSticker(null)} />
      {selectedPack && (
        <StickerUploadDialog
          packId={selectedPack.packId}
          packLabel={selectedPack.displayName}
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
        />
      )}
    </div>
  );
}

function PackRow({
  pack,
  selected,
  onSelect,
  onEdit,
  readOnly,
}: {
  pack: MediaStickerPack;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  readOnly: boolean;
}) {
  const confirm = useConfirm();
  const mutation = useSetStickerPackListed();
  const isActive = pack.status === 'ACTIVE';

  const onToggle = async () => {
    const delisting = isActive;
    const { confirmed } = await confirm({
      title: delisting ? 'Delist sticker pack' : 'Relist sticker pack',
      description: delisting
        ? `Remove the "${pack.displayName}" tab and all ${pack.activeStickerCount} live stickers in it from the mobile picker? Existing drafts retain access to their artwork.`
        : `Return "${pack.displayName}" and its listed stickers to the mobile picker?`,
      confirmText: delisting ? 'Delist pack' : 'Relist pack',
      variant: delisting ? 'destructive' : 'default',
    });
    if (!confirmed) return;
    try {
      await mutation.mutateAsync({ packId: pack.packId, listed: !delisting });
      toast.success(delisting ? 'Pack delisted' : 'Pack relisted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update the pack');
    }
  };

  return (
    <div
      className={cn(
        'rounded-md border p-3 transition-colors',
        selected ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted/50',
        !isActive && 'opacity-60',
      )}
    >
      <button type="button" onClick={onSelect} className="flex w-full items-center gap-3 text-left">
        {pack.tabIconUrl ? (
          <img
            src={pack.tabIconUrl}
            loading="lazy"
            alt=""
            className="size-9 shrink-0 rounded object-contain"
            title="This image is the pack's tab icon in the mobile picker"
          />
        ) : (
          <div className="size-9 shrink-0 rounded border border-dashed" />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{pack.displayName}</div>
          <div className="truncate font-mono text-xs text-muted-foreground">{pack.packId}</div>
        </div>
      </button>
      <div className="mt-2 flex items-center gap-2">
        <CurationStatusBadge status={pack.status} />
        <StickerSourceBadge source={pack.source} />
        <span className="text-xs text-muted-foreground">
          {pack.activeStickerCount}
          {pack.delistedStickerCount > 0 ? ` (+${pack.delistedStickerCount} hidden)` : ''}
        </span>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" disabled={readOnly} onClick={onEdit}>
          Edit
        </Button>
        <Button
          variant={isActive ? 'ghost' : 'outline'}
          size="sm"
          disabled={readOnly || mutation.isPending}
          onClick={() => void onToggle()}
          className={isActive ? 'text-destructive hover:text-destructive' : undefined}
        >
          {isActive ? 'Delist' : 'Relist'}
        </Button>
      </div>
    </div>
  );
}

function StickerTile({
  sticker,
  readOnly,
  onEdit,
}: {
  sticker: MediaSticker;
  readOnly: boolean;
  onEdit: () => void;
}) {
  const confirm = useConfirm();
  const mutation = useSetStickerListed();
  const isActive = sticker.status === 'ACTIVE';

  const onToggle = async () => {
    const delisting = isActive;
    const { confirmed } = await confirm({
      title: delisting ? 'Delist sticker' : 'Relist sticker',
      description: delisting
        ? `Remove "${sticker.name}" from the mobile picker? Existing drafts retain access to this artwork.`
        : `Return "${sticker.stickerId}" to the mobile picker?`,
      confirmText: delisting ? 'Delist' : 'Relist',
      variant: delisting ? 'destructive' : 'default',
    });
    if (!confirmed) return;
    try {
      await mutation.mutateAsync({ stickerId: sticker.stickerId, listed: !delisting });
      toast.success(delisting ? 'Sticker delisted' : 'Sticker relisted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update the sticker');
    }
  };

  return (
    <div className={cn('group rounded-md border p-2', !isActive && 'opacity-50')}>
      {/* Checkerboard: stickers are transparent PNG/WebP and would otherwise
          be invisible against the panel background. */}
      <div className="flex h-24 items-center justify-center rounded bg-[linear-gradient(45deg,#8883_25%,transparent_25%,transparent_75%,#8883_75%),linear-gradient(45deg,#8883_25%,transparent_25%,transparent_75%,#8883_75%)] bg-[length:16px_16px] bg-[position:0_0,8px_8px]">
        {sticker.thumbnailUrl ? (
          <img
            src={sticker.thumbnailUrl}
            alt={sticker.name}
            loading="lazy"
            decoding="async"
            className="max-h-full max-w-full"
          />
        ) : (
          <ImageOff aria-label="No thumbnail" className="size-6 text-muted-foreground" />
        )}
      </div>
      <div className="mt-2 truncate font-mono text-xs" title={sticker.stickerId}>
        {sticker.name}
      </div>
      <div className="text-xs text-muted-foreground">
        {sticker.originalWidth}×{sticker.originalHeight}
      </div>
      <div className="mt-1 flex items-center gap-1">
        <Button variant="ghost" size="sm" disabled={readOnly} onClick={onEdit}>
          Edit details
        </Button>
        <a
          href={sticker.downloadUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-primary underline"
        >
          Original
        </a>
      </div>
      <Button
        variant="ghost"
        size="sm"
        disabled={readOnly || mutation.isPending}
        onClick={() => void onToggle()}
        className={cn('mt-1 w-full', isActive && 'text-destructive hover:text-destructive')}
      >
        {isActive ? 'Delist' : 'Relist'}
      </Button>
    </div>
  );
}
